<!--
  @component
  Generates an HTML x-axis, useful for server-side rendered charts.  This component is also configured to detect if your x-scale is an ordinal scale. If so, it will place the markers in the middle of the bandwidth.
 -->
<script>
	import { getContext } from "svelte";

	import { percent, px } from "../utils";

	const { xScale, yScale } = getContext("LayerCake");

	/** @type {Boolean} [gridlines=true] - Extend lines from the ticks into the chart space. */
	export let gridlines = true;

	/** @type {Boolean} [tickMarks=false] - Show a vertical mark for each tick. */
	export let tickMarks = false;

	/** @type {Boolean} [baseline=false] – Show a solid line at the bottom. */
	export let baseline = false;

	// export let baselineStyle = "1px solid #202020";
	export let baselineWidth = 1;
	export let baselineStyle = "solid";
	export let baselineColor = "#202020";

	export let ticksPositionY = "bottom"; // can be "top", "bottom", or "baseline"

	export let tickHeight = 6;
	export let tickOffsetY = 4;

	/** @type {Boolean} [snapTicks=false] - Instead of centering the text on the first and the last items, align them to the edges of the chart. */
	export let snapTicks = false;

	/** @type {Function} [formatTick=d => d] - A function that passes the current tick value and expects a nicely formatted value in return. */
	export let formatTick = (d, i) => d;

	/** @type {Number|Array|Function} [ticks] - If this is a number, it passes that along to the [d3Scale.ticks](https://github.com/d3/d3-scale) function. If this is an array, hardcodes the ticks to those values. If it's a function, passes along the default tick values and expects an array of tick values in return. If nothing, it uses the default ticks supplied by the D3 function. */
	export let ticks = undefined;

	/** @type {Number} [yTick=7] - The distance from the baseline to place each tick value, in pixels. */
	export let yTick = 7;

	export let tickBackgroundColor = undefined;

	$: isBandwidth = typeof $xScale.bandwidth === "function";

	$: tickVals = Array.isArray(ticks)
		? ticks
		: isBandwidth
		? $xScale.domain()
		: typeof ticks === "function"
		? ticks($xScale.ticks())
		: $xScale.ticks(ticks);
</script>

<div class="axis x-axis" class:snapTicks>
	{#if baseline === true}
		<div
			class="baseline"
			style:width={"100%"}
			style:top={percent($yScale(0))}
			style:border-top-style={baselineStyle}
			style:border-top-width={px(baselineWidth)}
			style:border-top-color={baselineColor}
		/>
	{/if}
	{#each tickVals as tick, i}
		{@const isLast = i === tickVals.length - 1}
		{@const addBandwidth = !snapTicks && isBandwidth}
		{#if gridlines !== false}
			<div class="gridline" style="left:{$xScale(tick)}%;top: 0px;bottom: 0;" />
		{/if}
		{#if tickMarks === true}
			<div
				class="tick-mark"
				style:left={percent(
					$xScale(tick) +
						(addBandwidth ? $xScale.bandwidth() / 2 : 0) +
						(snapTicks && isLast && isBandwidth ? $xScale.bandwidth() : 0)
				)}
				style:height="{tickHeight}px"
				style:top="calc({percent($yScale(0))} + {tickOffsetY}px)"
			/>
		{/if}
		<div
			class="tick tick-{i} y-{ticksPositionY}"
			style:left={percent(
				$xScale(tick) +
					(addBandwidth ? $xScale.bandwidth() / 2 : 0) +
					(snapTicks && isLast && isBandwidth ? $xScale.bandwidth() : 0)
			)}
			style:top="calc({percent(
				ticksPositionY === "baseline" ? $yScale(0) : ticksPositionY === "top" ? 0 : 100
			)} + {tickOffsetY}px)"
		>
			<div
				class="text"
				style:background-color={tickBackgroundColor}
				style:top={px(ticksPositionY === "top" ? -yTick : yTick)}
			>
				{formatTick(tick, i)}
			</div>
		</div>
	{/each}
</div>

<style>
	.axis,
	.tick,
	.tick-mark,
	.gridline,
	.baseline {
		position: absolute;
	}
	.axis {
		width: 100%;
		height: 100%;
	}
	.tick {
		/* font-size: 12px;
		line-height: 15px; */
	}

	.gridline {
		border-left: 1px dashed #aaa;
	}

	.tick-mark {
		border-left: 1px solid #9b9b9b;
	}
	.baseline {
		border-top: 1px solid #2a2a2a;
	}

	.tick .text {
		color: #9b9b9b;
		opacity: 0.6;
		position: relative;
		white-space: nowrap;
		transform: translateX(-50%);
	}

	.tick.y-top .text {
		transform: translate(-50%, -100%);
	}

	/* This looks a little better at 40 percent than 50 */
	.axis.snapTicks .tick:last-child {
		transform: translateX(-50%);
	}
	.axis.snapTicks .tick.tick-0 {
		transform: translateX(50%);
	}
</style>
